/*
 * Copyright (c) 2026 Anvilor Solutions LLC - All Rights Reserved.
 *
 * The information and source code contained herein is the exclusive property of
 * Anvilor Solutions LLC and may not be disclosed, examined, or reproduced in whole
 * or in part without the explicit written authorization from Anvilor Solutions LLC.
 */

import { EelOperator, EelType, Evaluator } from "@anvilor/ngx-eel";
import { combineLatest, defer, Observable } from "rxjs";
import { map, take } from "rxjs/operators";
import { AnvilorFormsApp } from "../anvilor-forms-app-store.service";
import { RuiInstance } from "@anvilor/ngx-rui";

export const SUBMIT_FORM_OPERATOR_ID = "submit_form";
export const SUBMIT_FORM_DISABLED_OPERATOR_ID = "submit_form_disabled";

/**
 * The standard submit gating, mirroring the legacy `anvilor_forms:submit_button`
 * disable logic: posting in flight, form closed, RUI validation failing (unless
 * force-enabled), or an unsatisfied captcha challenge.
 *
 * Deferred so the RuiInstance validity stream is read at subscription time (it is
 * reassigned when a legacy config loads).
 */
export function submitDisabled$(
  app: AnvilorFormsApp,
  rui: RuiInstance
): Observable<boolean> {
  return defer(() =>
    combineLatest([
      app.submit.isPosting$,
      rui.validatedData$,
      app.openClose.formIsClosed$,
      app.captcha.canSubmit$,
      app.submit.forceEnableSubmit$,
    ]).pipe(
      map(
        ([isPosting, ruiAllValid, formIsClosed, validCaptcha, forceEnableSubmit]) =>
          isPosting ||
          formIsClosed ||
          (!ruiAllValid && !forceEnableSubmit) ||
          !validCaptcha
      )
    )
  );
}

/**
 * `{"#submit_form": {}}` - submit the form response. This is what a v1 config's
 * submit button wires to via `on_click_action` (the v1 counterpart of the legacy
 * `anvilor_forms:submit_button` control). Applies the same gating as
 * [[submitDisabled$]] and evaluates to whether a submission was started.
 */
export class SubmitFormOperator extends EelOperator<EelType, {}> {
  constructor(private app: AnvilorFormsApp, private rui: RuiInstance) {
    super();
  }

  get id(): string {
    return SUBMIT_FORM_OPERATOR_ID;
  }

  eval$(evaluator: Evaluator, node: {}): Observable<any> {
    return submitDisabled$(this.app, this.rui).pipe(
      take(1),
      map((disabled) => {
        if (disabled) {
          return false;
        }
        this.app.submit.doSubmit();
        return true;
      })
    );
  }
}

/**
 * `{"#submit_form_disabled": {}}` - reactive boolean for a submit button's
 * `disabled` expression, so v1 submit buttons gate exactly like the legacy one.
 */
export class SubmitFormDisabledOperator extends EelOperator<EelType, {}> {
  constructor(private app: AnvilorFormsApp, private rui: RuiInstance) {
    super();
  }

  get id(): string {
    return SUBMIT_FORM_DISABLED_OPERATOR_ID;
  }

  eval$(evaluator: Evaluator, node: {}): Observable<any> {
    return submitDisabled$(this.app, this.rui);
  }
}
